/**
 * AftershockSkillEffect — Earth tree stone_fist path passive (stone_fist_path_passive).
 *
 * Every Stone Fist or Hammer impact leaves a tremor zone at the contact point
 * for 3 seconds. Enemies stepping into the tremor zone take 4 damage and are
 * staggered for 0.2 seconds. Multiple tremor zones can coexist.
 *
 * The skill itself does not fire on a tick — it exposes `createTremorZone(position)`
 * which the game layer (or HammerSkillEffect / StoneFistSkillEffect integration hook)
 * calls after each confirmed hit.
 *
 * ZoneSystem handles the entry-triggered damage and stagger. Aftershock creates
 * the zone with `entryDamage`, `entryStaggerMs`, and `blocksMovement: false` baked
 * into the zone config. The ZoneSystem tremor zone type fires a callback on each
 * entity entry (not on every tick).
 *
 * UpgradeEffectResolver key(s) on scope 'aftershock':
 * - tremorDurationMs: additive, base 3000
 * - tremorDamage: additive, base 4
 * - tremorStaggerMs: additive, base 200
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Stone Fist Path — Aftershock
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface AftershockDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly zones: {
    create(config: {
      type: string;
      position: { x: number; y: number };
      radiusTiles: number;
      durationMs: number;
      entryDamage: number;
      entryStaggerMs: number;
      blocksMovement: boolean;
      sourceSkillId: string;
    }): string;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AftershockConfig {
  /** Tremor zone radius in tiles. */
  readonly radiusTiles: number;
}

const DEFAULT_CONFIG: AftershockConfig = {
  radiusTiles: 0.5,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class AftershockSkillEffect implements SkillEffect {
  readonly skillId = 'stone_fist_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: AftershockDeps;
  private readonly config: AftershockConfig;

  private _zonesCreatedCount = 0;

  constructor(deps: AftershockDeps, config: Partial<AftershockConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getter ---

  get zonesCreatedCount(): number { return this._zonesCreatedCount; }
  get radiusTiles(): number { return this.config.radiusTiles; }

  // --- Tremor zone creation (called by game layer on each Stone Fist / Hammer hit) ---

  /**
   * Creates a tremor zone at the given world position.
   * Called by the post-hit callback after Stone Fist or Hammer confirms a hit.
   * Returns the zone ID assigned by ZoneSystem.
   */
  createTremorZone(position: { x: number; y: number }): string {
    const durationMs = this.deps.resolver.getAdditiveEffect('stone_fist_path_passive', 'tremorDurationMs');
    const entryDamage = this.deps.resolver.getAdditiveEffect('stone_fist_path_passive', 'tremorDamage');
    const entryStaggerMs = this.deps.resolver.getAdditiveEffect('stone_fist_path_passive', 'tremorStaggerMs');

    const zoneId = this.deps.zones.create({
      type: 'tremor',
      position,
      radiusTiles: this.config.radiusTiles,
      durationMs,
      entryDamage,
      entryStaggerMs,
      blocksMovement: false,
      sourceSkillId: 'aftershock',
    });

    this._zonesCreatedCount++;
    return zoneId;
  }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — no per-frame logic
  }

  destroy(): void {
    // No owned resources
  }
}
